import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI, contractsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Plus, ChevronLeft, ChevronRight, Wrench, Trash2, Pencil, Filter, X, Lock, RotateCcw } from 'lucide-react';
import { EntityLink } from '@/components/EntityLink';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  released: 'bg-blue-50 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const PRIORITY_COLORS = {
  low: 'bg-slate-50 text-slate-600',
  normal: 'bg-blue-50 text-blue-600',
  high: 'bg-amber-50 text-amber-600',
  urgent: 'bg-red-50 text-red-600',
};

export default function WorkOrdersPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 25;

  // Advanced filters
  const [bomItemFilter, setBomItemFilter] = useState('');
  const [customerWoFilter, setCustomerWoFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [challanFilter, setChallanFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterItems, setFilterItems] = useState([]);
  const [filterCustomers, setFilterCustomers] = useState([]);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [openPOs, setOpenPOs] = useState([]);
  const [challanLineItems, setChallanLineItems] = useState([]);
  const [challanLineItemsLoading, setChallanLineItemsLoading] = useState(false);
  const [bomMaterials, setBomMaterials] = useState([]);
  const [bomMaterialsLoading, setBomMaterialsLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    item_id: '', route_id: '', quantity: '', planned_start_date: new Date().toISOString().split('T')[0],
    planned_end_date: '', priority: 'normal', route_card_number: '', remarks: '', customer_id: '', customer_name: '', 
    customer_wo_number: '', project_number: '',
    po_id: '', po_number: '', challan_line_items: [], selected_lots: [], own_consumption: false,
  });
  const [creating, setCreating] = useState(false);

  // Production receipt dialog
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptOrder, setReceiptOrder] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ produced_quantity: '', scrap_quantity: '0', rejected_quantity: '0', remarks: '' });
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [traceEntries, setTraceEntries] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editCustomers, setEditCustomers] = useState([]);
  const [editOpenPOs, setEditOpenPOs] = useState([]);
  const [editing, setEditing] = useState(false);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter === 'active') {
        params.status = 'released,in_progress';
      } else if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (search.trim()) params.search = search.trim();
      if (bomItemFilter) params.bom_item_id = bomItemFilter;
      if (customerWoFilter.trim()) params.customer_wo_number = customerWoFilter.trim();
      if (customerFilter) params.customer_id = customerFilter;
      if (challanFilter.trim()) params.challan_number = challanFilter.trim();
      const res = await inventoryAPI.listWorkOrders(params);
      setOrders(res.data.work_orders);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load work orders');
    } finally { setLoading(false); }
  };

  // Load filter options
  const loadFilterOptions = async () => {
    try {
      const [itemsRes, custRes] = await Promise.all([
        inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' }),
        contractsAPI.activeCustomers(),
      ]);
      setFilterItems(itemsRes.data.items || []);
      setFilterCustomers(custRes.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadOrders();
  }, [page, statusFilter, search, bomItemFilter, customerWoFilter, customerFilter, challanFilter]);

  const clearFilters = () => {
    setBomItemFilter('');
    setCustomerWoFilter('');
    setCustomerFilter('');
    setChallanFilter('');
    setSearch('');
    setStatusFilter('active');
    setPage(1);
  };

  const hasActiveFilters = bomItemFilter || customerWoFilter || customerFilter || challanFilter;

  const openCreate = async () => {
    try {
      const [itemsRes, routesRes, custRes] = await Promise.all([
        inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc', make_or_buy: 'make' }),
        inventoryAPI.listRoutes({}),
        contractsAPI.activeCustomers(),
      ]);
      setItems(itemsRes.data.items);
      const routesData = routesRes.data;
      setRoutes(Array.isArray(routesData) ? routesData : routesData.routes || []);
      setCustomers(custRes.data || []);
      setOpenPOs([]);
      setCreateOpen(true);
    } catch { toast.error('Failed to load data'); }
  };

  const loadOpenPOs = async (customerId) => {
    if (!customerId) { setOpenPOs([]); return; }
    try {
      const res = await contractsAPI.listOpenPOs({ customer_id: customerId });
      setOpenPOs(res.data || []);
    } catch { setOpenPOs([]); }
  };

  const handleItemChange = (itemId) => {
    const it = items.find(i => i.id === itemId);
    const stillFixedAsset = it && it.item_type === 'Fixed Asset';
    setCreateForm(f => ({ ...f, item_id: itemId, route_id: '', challan_line_items: [], selected_lots: [], quantity: '', own_consumption: stillFixedAsset ? f.own_consumption : false }));
    setChallanLineItems([]);
    setBomMaterials([]);
  };

  const handleRouteChange = async (routeId) => {
    setCreateForm(f => ({ ...f, route_id: routeId, challan_line_items: [], selected_lots: [] }));
    setChallanLineItems([]);
    setBomMaterials([]);
    
    if (routeId) {
      setChallanLineItemsLoading(true);
      setBomMaterialsLoading(true);
      try {
        const [challanRes, bomRes] = await Promise.all([
          inventoryAPI.getChallanLineItemsForRoute(routeId),
          inventoryAPI.getBomStoreLots(routeId),
        ]);
        setChallanLineItems(challanRes.data.line_items || []);
        setBomMaterials(bomRes.data.bom_materials || []);
      } catch (err) {
        console.error('Failed to load route data:', err);
        setChallanLineItems([]);
        setBomMaterials([]);
      } finally {
        setChallanLineItemsLoading(false);
        setBomMaterialsLoading(false);
      }
    }
  };

  const computeChallanTotal = (challanItems) => {
    return challanItems.reduce((s, li) => s + (li.selected_quantity ?? li.quantity), 0);
  };

  const toggleChallanLineItem = (lineItem) => {
    setCreateForm(f => {
      const existing = f.challan_line_items || [];
      const key = `${lineItem.challan_id}-${lineItem.line_index}`;
      const isSelected = existing.some(li => `${li.challan_id}-${li.line_index}` === key);
      
      let newChallanItems;
      if (isSelected) {
        newChallanItems = existing.filter(li => `${li.challan_id}-${li.line_index}` !== key);
      } else {
        const selectedQty = lineItem.available_quantity != null ? lineItem.available_quantity : lineItem.ok_quantity || lineItem.quantity;
        newChallanItems = [...existing, { ...lineItem, selected_quantity: selectedQty }];
      }
      const newQty = computeRmDerivedQty(f.selected_lots || [], newChallanItems);
      return { ...f, challan_line_items: newChallanItems, quantity: newQty > 0 ? String(newQty) : '' };
    });
  };

  const updateChallanLineQty = (lineItem, newQty) => {
    const maxQty = lineItem.available_quantity != null ? lineItem.available_quantity : lineItem.ok_quantity || lineItem.quantity;
    const qty = Math.min(Math.max(0, parseFloat(newQty) || 0), maxQty);
    setCreateForm(f => {
      const newChallanItems = (f.challan_line_items || []).map(li => {
        if (li.challan_id === lineItem.challan_id && li.line_index === lineItem.line_index) {
          return { ...li, selected_quantity: qty };
        }
        return li;
      });
      const newQtyVal = computeRmDerivedQty(f.selected_lots || [], newChallanItems);
      return { ...f, challan_line_items: newChallanItems, quantity: newQtyVal > 0 ? String(newQtyVal) : '' };
    });
  };

  const isChallanLineItemSelected = (lineItem) => {
    const selected = createForm.challan_line_items || [];
    return selected.some(li => li.challan_id === lineItem.challan_id && li.line_index === lineItem.line_index);
  };

  // --- BOM Lot selection handlers ---
  const isSubcontractItem = (mat) => mat.item_type === 'Job Work Item' || mat.make_or_buy === 'receive_from_customer';
  const isPurchasedItem = (mat) => !isSubcontractItem(mat);

  const toggleLot = (lot, bomItem) => {
    setCreateForm(f => {
      const existing = f.selected_lots || [];
      const isSelected = existing.some(l => l.lot_id === lot.lot_id);
      let newLots;
      if (isSelected) {
        newLots = existing.filter(l => l.lot_id !== lot.lot_id);
      } else {
        newLots = [...existing, { ...lot, item_name: bomItem.item_name, item_unique_id: bomItem.item_unique_id, selected_quantity: lot.remaining_quantity }];
      }
      // Auto-update WO qty based on selected RM lots and BOM ratios
      const newQty = computeRmDerivedQty(newLots, f.challan_line_items || []);
      return { ...f, selected_lots: newLots, quantity: newQty > 0 ? String(newQty) : f.quantity };
    });
  };

  const updateLotQty = (lotId, newQty, maxQty) => {
    const qty = Math.min(Math.max(0, parseFloat(newQty) || 0), maxQty);
    setCreateForm(f => {
      const newLots = (f.selected_lots || []).map(l => l.lot_id === lotId ? { ...l, selected_quantity: qty } : l);
      const newWoQty = computeRmDerivedQty(newLots, f.challan_line_items || []);
      return { ...f, selected_lots: newLots, quantity: newWoQty > 0 ? String(newWoQty) : f.quantity };
    });
  };

  const isLotSelected = (lotId) => {
    return (createForm.selected_lots || []).some(l => l.lot_id === lotId);
  };

  // Compute max producible qty from selected purchased RM lots based on BOM ratios
  const computeRmDerivedQty = (selectedLots, challanItems) => {
    const purchasedMats = bomMaterials.filter(isPurchasedItem);
    if (purchasedMats.length === 0) {
      // No purchased RM in BOM — qty driven by challan only
      return computeChallanTotal(challanItems);
    }

    // Group selected lots by item_id and sum their selected_quantity
    const lotsByItem = {};
    for (const lot of selectedLots) {
      if (!lotsByItem[lot.item_id]) lotsByItem[lot.item_id] = 0;
      lotsByItem[lot.item_id] += (lot.selected_quantity || 0);
    }

    // For each purchased BOM material, compute how many units can be made
    let rmQty = Infinity;
    let hasAnySelection = false;
    for (const mat of purchasedMats) {
      const totalSelected = lotsByItem[mat.item_id] || 0;
      if (totalSelected > 0) {
        hasAnySelection = true;
        const possibleFromMat = mat.quantity_per_unit > 0 ? Math.floor(totalSelected / mat.quantity_per_unit) : Infinity;
        rmQty = Math.min(rmQty, possibleFromMat);
      }
    }

    if (!hasAnySelection) rmQty = 0;

    // In mixed BOM (both challan + purchased RM), use the bottleneck
    const challanTotal = computeChallanTotal(challanItems);
    if (challanTotal > 0 && rmQty > 0) {
      return Math.min(challanTotal, rmQty);
    }
    return challanTotal > 0 ? challanTotal : rmQty;
  };

  const filteredRoutes = routes.filter(r => r.status !== 'suspended' && (!createForm.item_id || r.item_id === createForm.item_id));

  const selectedItem = items.find(i => i.id === createForm.item_id);
  const canOwnConsumption = isAdmin && selectedItem?.item_type === 'Fixed Asset';

  const handleCreate = async () => {
    if (!createForm.item_id || !createForm.route_id || !createForm.quantity) {
      toast.error('Item, route, and quantity are required');
      return;
    }
    if (!createForm.own_consumption && !createForm.customer_id) {
      toast.error('Customer is mandatory for work order creation (required for dispatch traceability)');
      return;
    }
    const challanItems = createForm.challan_line_items || [];
    const isJobwork = (challanItems && challanItems.length > 0)
      || !!createForm.inbound_challan_id;
    if (!createForm.own_consumption && !isJobwork && !createForm.po_id) {
      toast.error('Purchase Order is mandatory for own-manufactured items (no customer inbound challan). Select the customer PO.');
      return;
    }
    if (challanItems.length > 0) {
      const challanTotal = computeChallanTotal(challanItems);
      if (parseFloat(createForm.quantity) > challanTotal) {
        toast.error(`Quantity (${createForm.quantity}) cannot exceed challan total (${challanTotal})`);
        return;
      }
    }
    setCreating(true);
    try {
      await inventoryAPI.createWorkOrder({ ...createForm, quantity: parseFloat(createForm.quantity) });
      toast.success('Work order created');
      setCreateOpen(false);
      setCreateForm({ item_id: '', route_id: '', quantity: '', planned_start_date: new Date().toISOString().split('T')[0], planned_end_date: '', priority: 'normal', route_card_number: '', remarks: '', customer_id: '', customer_name: '', customer_wo_number: '', project_number: '', po_id: '', po_number: '', challan_line_items: [], selected_lots: [], own_consumption: false });
      setChallanLineItems([]);
      setBomMaterials([]);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setCreating(false); }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await inventoryAPI.updateWorkOrder(orderId, { status: newStatus });
      toast.success('Status updated');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const openReceipt = (order) => {
    setReceiptOrder(order);
    // Auto-calculate expected scrap from BOM output_items (per-unit × produced or remaining qty)
    const perUnitScrap = (order.output_items || [])
      .filter(o => o.output_type === 'scrap')
      .reduce((sum, o) => sum + (o.quantity || 0), 0);
    const remainingQty = (order.quantity || 0) - (order.produced_quantity || 0);
    const expectedScrap = perUnitScrap * (remainingQty > 0 ? remainingQty : order.quantity || 1);
    setReceiptForm({ produced_quantity: remainingQty > 0 ? String(remainingQty) : '', scrap_quantity: expectedScrap > 0 ? String(expectedScrap) : '0', rejected_quantity: '0', remarks: '' });

    // Auto-populate trace entries from challan_line_items.
    // Greedy FIFO distribution: fill the first lines completely with the
    // produced qty, leave the remaining lines at 0. The user can then re-
    // allocate manually if they actually produced from a different subset.
    const clis = order.challan_line_items || [];
    const custWoNum = order.customer_wo_number || '';
    if (clis.length > 0) {
      let toAllocate = Math.max(0, remainingQty);
      const traces = clis.map(cli => {
        const challanNum = cli.challan_number || '';
        const lineIdx = cli.line_index ?? 0;
        const maxQty = cli.selected_quantity || cli.quantity || 0;

        // Prefer the actual customer trace id (customer WO#) recorded on the
        // inbound challan line. Fall back to challan #N or customer WO when
        // the inbound challan didn't carry a trace id.
        const challanTraceId = (cli.traceability_id || '').trim();
        const challanTraceDesc = (cli.traceability_id_description || '').trim();

        let traceId = '';
        let traceLabel = '';
        let traceType = 'challan_line';
        if (challanTraceId) {
          traceId = challanTraceId;
          traceLabel = challanTraceDesc
            ? `Trace ${challanTraceId} · ${challanTraceDesc}`
            : `Trace ${challanTraceId}`;
          if (challanNum) traceLabel += ` · DC ${challanNum} #${lineIdx + 1}`;
        } else if (challanNum) {
          traceId = `${challanNum}-#${lineIdx + 1}`;
          traceLabel = `DC: ${challanNum} Line #${lineIdx + 1}`;
          if (custWoNum) {
            traceId += ` / ${custWoNum}`;
            traceLabel += ` | CWO: ${custWoNum}`;
          }
          if (order.wo_number) {
            traceId += ` / ${order.wo_number}`;
          }
        } else if (custWoNum) {
          traceType = 'customer_wo';
          traceId = custWoNum;
          traceLabel = `Customer WO: ${custWoNum}`;
        } else {
          traceType = 'auto';
          traceId = `AUTO-${Date.now()}-${lineIdx}`;
          traceLabel = `Auto: Line #${lineIdx + 1}`;
        }

        // Greedy fill — never split into fractions just to make totals match
        const allocated = Math.min(maxQty, toAllocate);
        toAllocate -= allocated;

        return {
          trace_id: traceId,
          trace_type: traceType,
          trace_label: traceLabel,
          quantity: allocated,
          quarantined_quantity: 0,
          max_quantity: maxQty,
          challan_id: cli.challan_id || '',
          challan_number: challanNum,
          challan_line_index: lineIdx,
          item_name: cli.item_name || '',
          customer_challan_number: cli.customer_challan_number || '',
          customer_po_number: cli.purchase_order_number || '',
        };
      });
      setTraceEntries(traces);
    } else if (order.customer_wo_number) {
      // Single customer WO — create one trace entry
      setTraceEntries([{
        trace_id: order.customer_wo_number + (order.wo_number ? ` / ${order.wo_number}` : ''),
        trace_type: 'customer_wo',
        trace_label: `Customer WO: ${order.customer_wo_number}`,
        quantity: remainingQty > 0 ? remainingQty : '',
        quarantined_quantity: 0,
        max_quantity: order.quantity || 0,
        challan_id: '', challan_number: '', challan_line_index: null,
      }]);
    } else {
      setTraceEntries([]);
    }
    setReceiptOpen(true);
  };

  const handleProductionReceipt = async () => {
    if (!receiptForm.produced_quantity && !receiptForm.rejected_quantity) {
      toast.error('Produced or Quarantined quantity is required'); return;
    }
    const produced = parseFloat(receiptForm.produced_quantity || 0);
    const quarantined = parseFloat(receiptForm.rejected_quantity || 0);

    // Validate trace entries if present
    if (traceEntries.length > 0) {
      const traceOkTotal = traceEntries.reduce((s, te) => s + (parseFloat(te.quantity) || 0), 0);
      const traceQTotal = traceEntries.reduce((s, te) => s + (parseFloat(te.quarantined_quantity) || 0), 0);
      if (Math.abs(traceOkTotal - produced) > 0.01) {
        toast.error(`Trace OK quantities (${traceOkTotal}) must equal produced quantity (${produced})`);
        return;
      }
      if (Math.abs(traceQTotal - quarantined) > 0.01) {
        toast.error(`Trace Quarantine quantities (${traceQTotal}) must equal Quarantined Quantity (${quarantined})`);
        return;
      }
      for (const te of traceEntries) {
        if (!te.trace_id) { toast.error('Each trace entry must have a trace ID'); return; }
        const ok = parseFloat(te.quantity) || 0;
        const qq = parseFloat(te.quarantined_quantity) || 0;
        if (ok < 0 || qq < 0) { toast.error(`Trace quantities must be non-negative for ${te.trace_label || te.trace_id}`); return; }
        if (ok === 0 && qq === 0) { toast.error(`Trace ${te.trace_label || te.trace_id}: enter OK or Quarantine qty > 0 (or remove the row)`); return; }
        // Validate against issued (max) quantity from challan line
        if (te.max_quantity && (ok + qq) > te.max_quantity + 0.01) {
          toast.error(`Trace qty OK+Q (${ok + qq}) for "${te.trace_label || te.trace_id}" exceeds issued qty (${te.max_quantity})`);
          return;
        }
      }
    }

    setReceiptLoading(true);
    try {
      const payload = {
        produced_quantity: produced,
        scrap_quantity: parseFloat(receiptForm.scrap_quantity || 0),
        // UI label is "Quarantined" — routed to QUARANTINE + auto-MRB case
        quarantined_quantity: quarantined,
        remarks: receiptForm.remarks,
        trace_entries: traceEntries
          .filter(te => (parseFloat(te.quantity) || 0) > 0 || (parseFloat(te.quarantined_quantity) || 0) > 0)
          .map(te => ({
            trace_id: te.trace_id,
            trace_type: te.trace_type,
            trace_label: te.trace_label,
            quantity: parseFloat(te.quantity) || 0,
            quarantined_quantity: parseFloat(te.quarantined_quantity) || 0,
            challan_id: te.challan_id || '',
            challan_number: te.challan_number || '',
            challan_line_index: te.challan_line_index,
            customer_wo_number: te.customer_wo_number || '',
            customer_challan_number: te.customer_challan_number || '',
            customer_po_number: te.customer_po_number || '',
          })),
      };
      const res = await inventoryAPI.productionReceipt(receiptOrder.id, payload);
      const consumed = res.data.consumed_materials || [];
      const traceCount = res.data.trace_lots?.length || 0;
      const consumedStr = consumed.length > 0 ? ` Materials consumed: ${consumed.map(c => `${c.item_name}(${c.consumed})`).join(', ')}` : '';
      const traceStr = traceCount > 0 ? ` ${traceCount} trace lot(s) created.` : '';
      toast.success(`Production receipt recorded.${consumedStr}${traceStr}`);
      setReceiptOpen(false);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record receipt');
    } finally { setReceiptLoading(false); }
  };

  const totalPages = Math.ceil(total / limit);

  const handleDeleteWO = async () => {
    if (!deleteTarget) return;
    try {
      await inventoryAPI.deleteWorkOrder(deleteTarget.id);
      toast.success(`Work order ${deleteTarget.wo_number} deleted`);
      setDeleteTarget(null);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete work order');
      setDeleteTarget(null);
    }
  };

  // Admin-only force close (for WOs that can't reach completion due to
  // unrecorded yield loss). Status -> completed; production figures untouched.
  const [forceCloseTarget, setForceCloseTarget] = useState(null);
  const [forceCloseReason, setForceCloseReason] = useState('');
  const [forceClosing, setForceClosing] = useState(false);

  const handleForceClose = async () => {
    if (!forceCloseTarget) return;
    const reason = forceCloseReason.trim();
    if (!reason) { toast.error('Please enter a reason for force-closing'); return; }
    setForceClosing(true);
    try {
      await inventoryAPI.forceCloseWorkOrder(forceCloseTarget.id, { remarks: reason });
      toast.success(`Work order ${forceCloseTarget.wo_number} force-closed`);
      setForceCloseTarget(null);
      setForceCloseReason('');
      if (editOpen) setEditOpen(false);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force-close work order');
    } finally { setForceClosing(false); }
  };

  // Admin-only reopen for a completed/closed WO (e.g. wrongly auto-completed).
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopening, setReopening] = useState(false);
  const handleReopen = async () => {
    if (!reopenTarget) return;
    setReopening(true);
    try {
      await inventoryAPI.reopenWorkOrder(reopenTarget.id);
      toast.success(`Work order ${reopenTarget.wo_number} reopened`);
      setReopenTarget(null);
      if (editOpen) setEditOpen(false);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen work order');
    } finally { setReopening(false); }
  };

  const openEdit = async (wo) => {
    setEditOrder(wo);
    setEditForm({
      quantity: wo.quantity || '',
      priority: wo.priority || 'normal',
      planned_start_date: wo.planned_start_date || '',
      planned_end_date: wo.planned_end_date || '',
      route_card_number: wo.route_card_number || '',
      customer_id: wo.customer_id || '',
      customer_name: wo.customer_name || '',
      customer_wo_number: wo.customer_wo_number || '',
      project_number: wo.project_number || '',
      po_id: wo.po_id || '',
      po_number: wo.po_number || '',
      remarks: wo.remarks || '',
    });
    try {
      const custRes = await contractsAPI.activeCustomers();
      setEditCustomers(custRes.data || []);
      if (wo.customer_id) {
        const poRes = await contractsAPI.listOpenPOs({ customer_id: wo.customer_id });
        setEditOpenPOs(poRes.data || []);
      } else {
        setEditOpenPOs([]);
      }
    } catch { setEditCustomers([]); setEditOpenPOs([]); }
    setEditOpen(true);
  };

  const loadEditPOs = async (customerId) => {
    if (!customerId) { setEditOpenPOs([]); return; }
    try {
      const res = await contractsAPI.listOpenPOs({ customer_id: customerId });
      setEditOpenPOs(res.data || []);
    } catch { setEditOpenPOs([]); }
  };

  const handleEdit = async () => {
    if (!editOrder) return;
    const payload = {};
    if (editForm.quantity !== '' && Number(editForm.quantity) !== editOrder.quantity) payload.quantity = parseFloat(editForm.quantity);
    if (editForm.priority !== editOrder.priority) payload.priority = editForm.priority;
    if (editForm.planned_start_date !== (editOrder.planned_start_date || '')) payload.planned_start_date = editForm.planned_start_date;
    if (editForm.planned_end_date !== (editOrder.planned_end_date || '')) payload.planned_end_date = editForm.planned_end_date;
    if (editForm.route_card_number !== (editOrder.route_card_number || '')) payload.route_card_number = editForm.route_card_number;
    if (editForm.customer_id !== (editOrder.customer_id || '')) { payload.customer_id = editForm.customer_id; payload.customer_name = editForm.customer_name; }
    if (editForm.project_number !== (editOrder.project_number || '')) payload.project_number = editForm.project_number;
    if (editForm.po_id !== (editOrder.po_id || '')) { payload.po_id = editForm.po_id; payload.po_number = editForm.po_number; }
    if (editForm.remarks !== (editOrder.remarks || '')) payload.remarks = editForm.remarks;

    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save');
      return;
    }
    setEditing(true);
    try {
      await inventoryAPI.updateWorkOrder(editOrder.id, payload);
      toast.success(`Work order ${editOrder.wo_number} updated`);
      setEditOpen(false);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update work order');
    } finally { setEditing(false); }
  };

  return (
    <div className="space-y-4" data-testid="work-orders-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Work Orders</h1>
          <p className="text-xs text-muted-foreground">{total} work orders</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="create-wo-btn">
          <Plus className="w-4 h-4 mr-1" /> New Work Order
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 space-y-3">
          {/* Primary filters row */}
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              placeholder="Search WO#, item, customer, BOM items..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="h-8 text-xs w-64"
              data-testid="wo-search"
            />
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="wo-status-filter">
                <SelectValue placeholder="Active (Default)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active (In Progress / Released)</SelectItem>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="released">Released</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 text-xs"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              data-testid="toggle-advanced-filters"
            >
              <Filter className="w-3.5 h-3.5 mr-1" />
              {showAdvancedFilters ? 'Hide' : 'More'} Filters
              {hasActiveFilters && <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">Active</Badge>}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={clearFilters}>
                <X className="w-3.5 h-3.5 mr-1" /> Clear All
              </Button>
            )}
          </div>
          
          {/* Advanced filters row */}
          {showAdvancedFilters && (
            <div className="flex flex-wrap gap-2 items-center pt-2 border-t">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground font-medium">BOM Item Consumed</label>
                <Select value={bomItemFilter || "all"} onValueChange={v => { setBomItemFilter(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger className="w-48 h-8 text-xs" data-testid="wo-bom-filter">
                    <SelectValue placeholder="Any Item" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Item</SelectItem>
                    {filterItems.map(item => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.unique_id} - {item.item_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground font-medium">Customer WO#</label>
                <Input
                  placeholder="Customer WO Number"
                  value={customerWoFilter}
                  onChange={e => { setCustomerWoFilter(e.target.value); setPage(1); }}
                  className="h-8 text-xs w-40"
                  data-testid="wo-customer-wo-filter"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground font-medium">Customer</label>
                <Select value={customerFilter || "all"} onValueChange={v => { setCustomerFilter(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger className="w-48 h-8 text-xs" data-testid="wo-customer-filter">
                    <SelectValue placeholder="Any Customer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Customer</SelectItem>
                    {filterCustomers.map(cust => (
                      <SelectItem key={cust.id} value={cust.id}>
                        {cust.customer_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground font-medium">Challan#</label>
                <Input
                  placeholder="Challan Number"
                  value={challanFilter}
                  onChange={e => { setChallanFilter(e.target.value); setPage(1); }}
                  className="h-8 text-xs w-36"
                  data-testid="wo-challan-filter"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Work Orders Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="work-orders-table">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">WO#</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Route Card#</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Customer WO#</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Finished Good</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Items Consumed</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Qty</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Challan Ref</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Customer</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Priority</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Status</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Planned</th>
                <th className="text-right p-2.5 text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-8 text-xs text-muted-foreground">Loading...</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-8 text-xs text-muted-foreground">No work orders</td></tr>
              ) : orders.map(wo => (
                <tr key={wo.id} className="border-b hover:bg-muted/50 transition-colors" data-testid={`wo-row-${wo.wo_number}`}>
                  <td className="p-2.5 font-mono text-xs font-medium">
                    <button
                      onClick={() => navigate(`/inventory/transactions?reference=${wo.wo_number}`)}
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      data-testid={`wo-link-${wo.wo_number}`}
                    >
                      {wo.wo_number}
                    </button>
                  </td>
                  <td className="p-2.5 text-xs font-mono text-muted-foreground">{wo.route_card_number || '-'}</td>
                  <td className="p-2.5 text-xs font-mono">{wo.customer_wo_number || '-'}</td>
                  <td className="p-2.5">
                    <div className="space-y-0.5">
                      {/* Primary output item (the main finished good) */}
                      <button
                        onClick={() => wo.item_id && navigate(`/inventory/items/${wo.item_id}`)}
                        className="text-left text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      >
                        <p className="text-xs font-medium">{wo.item_name}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{wo.item_unique_id}</p>
                      </button>
                      {/* Additional output items */}
                      {(wo.output_items || []).length > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-1">
                          +{wo.output_items.length} by-product(s)
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-2.5">
                    {(wo.bill_of_materials || []).length > 0 ? (
                      <div className="space-y-0.5 max-w-[200px]">
                        {wo.bill_of_materials.slice(0, 2).map((bom, idx) => (
                          <div key={idx} className="text-[10px] truncate" title={`${bom.item_name} (${bom.item_unique_id})`}>
                            <EntityLink type="item" id={bom.item_id} className="font-mono text-[10px]">{bom.item_unique_id}</EntityLink>
                            <span className="text-muted-foreground/70 ml-1">x{bom.quantity_per_unit}</span>
                          </div>
                        ))}
                        {wo.bill_of_materials.length > 2 && (
                          <span className="text-[9px] text-muted-foreground">+{wo.bill_of_materials.length - 2} more</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </td>
                  <td className="p-2.5 text-xs font-medium">{wo.produced_quantity || 0}/{wo.quantity}</td>
                  <td className="p-2.5 text-xs">
                    {(wo.challan_line_items || []).length > 0 ? (
                      <div className="space-y-0.5">
                        {wo.challan_line_items.slice(0, 2).map((cli, idx) => (
                          <div key={idx} className="text-[10px] font-mono text-muted-foreground">
                            <EntityLink type="challan" id={cli.challan_id} className="text-[10px] font-mono">{cli.challan_number}</EntityLink> #{cli.line_index + 1}
                          </div>
                        ))}
                        {wo.challan_line_items.length > 2 && (
                          <span className="text-[9px] text-muted-foreground">+{wo.challan_line_items.length - 2} more</span>
                        )}
                      </div>
                    ) : wo.inbound_challan_id ? (
                      <span className="text-[10px] font-mono text-muted-foreground">(legacy)</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="p-2.5 text-xs">{wo.customer_name || '-'}</td>
                  <td className="p-2.5">
                    <Badge variant="outline" className={`text-[10px] ${PRIORITY_COLORS[wo.priority] || ''}`}>{wo.priority}</Badge>
                  </td>
                  <td className="p-2.5">
                    <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[wo.status] || ''}`}>
                      {wo.status.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="p-2.5 text-xs font-mono">{wo.planned_start_date}</td>
                  <td className="p-2.5 text-right">
                    <div className="flex gap-1 justify-end">
                      {!['completed', 'closed'].includes(wo.status) && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700" title="Edit"
                          onClick={() => openEdit(wo)} data-testid={`edit-wo-${wo.wo_number}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {wo.status === 'draft' && (
                        <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => updateStatus(wo.id, 'released')}>Release</Button>
                      )}
                      {wo.status === 'released' && (
                        <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => updateStatus(wo.id, 'in_progress')}>Start</Button>
                      )}
                      {['released', 'in_progress'].includes(wo.status) && (
                        <Button size="sm" className="h-6 text-[10px]" onClick={() => openReceipt(wo)} data-testid={`receipt-btn-${wo.wo_number}`}>
                          Receipt
                        </Button>
                      )}
                      {isAdmin && ['released', 'in_progress'].includes(wo.status) && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-amber-600 hover:text-amber-800" title="Force close (admin)"
                          onClick={() => { setForceCloseTarget(wo); setForceCloseReason(''); }} data-testid={`force-close-wo-${wo.wo_number}`}>
                          <Lock className="w-3 h-3" />
                        </Button>
                      )}
                      {isAdmin && ['completed', 'closed'].includes(wo.status) && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800" title="Reopen (admin)"
                          onClick={() => setReopenTarget(wo)} data-testid={`reopen-wo-${wo.wo_number}`}>
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      )}
                      {(wo.produced_quantity || 0) === 0 && !['completed', 'closed'].includes(wo.status) && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" title="Delete"
                          onClick={() => setDeleteTarget(wo)} data-testid={`delete-wo-${wo.wo_number}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t">
            <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                <ChevronLeft className="w-3 h-3 mr-1" /> Prev
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                Next <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Work Order Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle>New Work Order</DialogTitle></DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1 pr-1" data-testid="wo-create-form-body">
            <div>
              <Label className="text-xs">Item (Make items only) *</Label>
              <SearchableSelect
                options={items.map(i => ({ value: i.id, label: `${i.unique_id} - ${i.item_name}`, sub: `${i.item_type} | ${i.category || 'No category'}`, searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''} ${i.description || ''}` }))}
                value={createForm.item_id}
                onSelect={v => handleItemChange(v)}
                placeholder="Search by ID, name, or description..."
                emptyText="No make items found."
                testId="wo-item-select"
              />
            </div>
            <div>
              <Label className="text-xs">Route *</Label>
              <SearchableSelect
                options={filteredRoutes.map(r => ({ value: r.id, label: r.route_name, sub: r.route_card_number ? `RC# ${r.route_card_number}` : '', searchStr: `${r.route_name} ${r.route_card_number || ''}` }))}
                value={createForm.route_id}
                onSelect={v => handleRouteChange(v)}
                placeholder="Search routes..."
                emptyText="No routes for this item."
                testId="wo-route-select"
              />
            </div>

            {canOwnConsumption && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5" data-testid="wo-own-consumption-box">
                <Checkbox
                  id="own-consumption"
                  checked={!!createForm.own_consumption}
                  onCheckedChange={(v) => setCreateForm(f => ({ ...f, own_consumption: !!v, customer_id: v ? '' : f.customer_id, customer_name: v ? '' : f.customer_name, po_id: v ? '' : f.po_id, po_number: v ? '' : f.po_number }))}
                  className="mt-0.5"
                  data-testid="wo-own-consumption-checkbox"
                />
                <Label htmlFor="own-consumption" className="text-xs leading-snug cursor-pointer">
                  <span className="font-semibold text-amber-800">Own consumption (Fixed Asset)</span>
                  <span className="block text-[10px] text-amber-700 font-normal mt-0.5">
                    Manufactured for internal use — exempts Customer &amp; Purchase Order. Admin only.
                  </span>
                </Label>
              </div>
            )}

            {/* === BOM Material Sections (conditional based on item types) === */}
            {createForm.route_id && (() => {
              const subcontractMats = bomMaterials.filter(isSubcontractItem);
              const purchasedMats = bomMaterials.filter(isPurchasedItem);
              const hasSubcontract = challanLineItems.length > 0 || subcontractMats.length > 0;
              const hasPurchased = purchasedMats.length > 0;
              return (
                <>
                  {/* Subcontract Challan Line Items (Job Work Items) */}
                  {hasSubcontract && (
                    <div>
                      <Label className="text-xs">Subcontract Challan Items (Job Work — received from supplier)</Label>
                      {challanLineItemsLoading ? (
                        <p className="text-xs text-muted-foreground py-2">Loading challan line items...</p>
                      ) : challanLineItems.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2">No matching challan line items for this route.</p>
                      ) : (
                        <div className="border rounded-md max-h-48 overflow-y-auto mt-1" data-testid="challan-section">
                          {challanLineItems.map((li, idx) => {
                            const isSelected = isChallanLineItemSelected(li);
                            const selectedLine = (createForm.challan_line_items || []).find(
                              sl => sl.challan_id === li.challan_id && sl.line_index === li.line_index
                            );
                            const maxQty = li.available_quantity != null ? li.available_quantity : li.ok_quantity || 0;
                            const hasAvailable = maxQty > 0;
                            return (
                              <div
                                key={`${li.challan_id}-${li.line_index}`}
                                className={`flex items-center gap-2 p-2 text-xs border-b last:border-b-0 transition-colors ${isSelected ? 'bg-blue-50' : hasAvailable ? 'hover:bg-muted/50' : 'bg-slate-50 opacity-60'}`}
                                data-testid={`challan-line-${idx}`}
                              >
                                <input 
                                  type="checkbox" 
                                  checked={isSelected}
                                  disabled={!hasAvailable && !isSelected}
                                  onChange={() => hasAvailable && toggleChallanLineItem(li)}
                                  className="w-3.5 h-3.5 cursor-pointer"
                                />
                                <span className="flex-1 min-w-0">
                                  <span className="block text-[11px] font-medium">{li.challan_number}</span>
                                  <span className="block text-[10px]">Line #{li.line_index + 1}: {li.item_name} {li.item_unique_id ? `(${li.item_unique_id})` : ''}</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    {(li.traceability_id || li.traceability_seed) && (
                                      <span className="text-blue-600 font-mono font-medium mr-1.5" data-testid={`challan-line-trace-${idx}`}>
                                        Trace: {li.traceability_id || li.traceability_seed}
                                      </span>
                                    )}
                                    {li.work_order_number && <span className="text-indigo-600 font-medium mr-1.5">Cust WO: {li.work_order_number}</span>}
                                    {li.customer_challan_number && <span className="text-slate-600 mr-1.5">Cust DC: {li.customer_challan_number}</span>}
                                    DC Qty: {li.quantity} {li.unit_of_measure}
                                    {' '}| Accepted: <span className={li.ok_quantity > 0 ? 'text-emerald-600 font-medium' : 'text-amber-500'}>{li.ok_quantity ?? 0}</span>
                                    {(li.rejected_quantity || 0) > 0 && <span className="text-red-500 ml-1">(Rej: {li.rejected_quantity})</span>}
                                    {li.allocated_quantity > 0 && <span className="text-amber-600 ml-1">(WO Used: {li.allocated_quantity})</span>}
                                    {' '}| Avl: <span className={hasAvailable ? 'text-emerald-600 font-medium' : 'text-red-500'}>{maxQty}</span>
                                  </span>
                                </span>
                                {isSelected && (
                                  <div className="flex items-center gap-1 shrink-0">
                                    <Label className="text-[10px] text-blue-600">Qty:</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0.01"
                                      max={maxQty}
                                      value={selectedLine?.selected_quantity ?? maxQty}
                                      onChange={e => updateChallanLineQty(li, e.target.value)}
                                      onClick={e => e.stopPropagation()}
                                      className="h-6 w-20 text-[10px] text-right font-mono"
                                      data-testid={`challan-line-qty-${idx}`}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {(createForm.challan_line_items || []).length > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {createForm.challan_line_items.length} line item(s) selected — Total qty: {createForm.challan_line_items.reduce((s, li) => s + (li.selected_quantity ?? li.quantity), 0).toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Purchased Raw Materials — Available Lots in STORE */}
                  {hasPurchased && (
                    <div>
                      <Label className="text-xs">Purchased Raw Materials — Available in Stores (net of WO commitments)</Label>
                      {bomMaterialsLoading ? (
                        <p className="text-xs text-muted-foreground py-2">Loading BOM materials...</p>
                      ) : (
                        <div className="border rounded-md mt-1 divide-y" data-testid="bom-materials-section">
                          {purchasedMats.map((mat, mIdx) => {
                            const matLots = mat.lots || [];
                            const selectedForMat = (createForm.selected_lots || []).filter(l => l.item_id === mat.item_id);
                            const selectedQtySum = selectedForMat.reduce((s, l) => s + (l.selected_quantity || 0), 0);
                            const netAvl = mat.net_available ?? Math.max(0, (mat.store_balance || 0) - (mat.committed_qty || 0));
                            return (
                              <div key={mat.item_id} className="p-2" data-testid={`bom-mat-${mIdx}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <div>
                                    <span className="text-[11px] font-medium">{mat.item_name}</span>
                                    <span className="text-[10px] font-mono text-muted-foreground ml-1.5">({mat.item_unique_id})</span>
                                  </div>
                                  <div className="text-[10px] text-right">
                                    <span className="text-muted-foreground">BOM: {mat.quantity_per_unit} {mat.unit_of_measure}/unit</span>
                                    <span className="mx-1">|</span>
                                    <span>Store: {mat.store_balance}</span>
                                    {(mat.committed_qty || 0) > 0 && (
                                      <span className="text-amber-600 ml-1">(WO: -{mat.committed_qty})</span>
                                    )}
                                    <span className="mx-1">|</span>
                                    <span>Net: <span className={`font-medium ${netAvl > 0 ? 'text-emerald-600' : 'text-red-500'}`} data-testid={`bom-mat-net-avl-${mIdx}`}>{netAvl}</span></span>
                                  </div>
                                </div>
                                {matLots.length === 0 ? (
                                  <p className="text-[10px] text-muted-foreground pl-4">No lots available in Stores</p>
                                ) : (
                                  <div className="ml-2 border rounded max-h-28 overflow-y-auto">
                                    {matLots.map((lot, lIdx) => {
                                      const selected = isLotSelected(lot.lot_id);
                                      const selLine = selectedForMat.find(l => l.lot_id === lot.lot_id);
                                      return (
                                        <div key={lot.lot_id} className={`flex items-center gap-2 px-2 py-1.5 text-[10px] border-b last:border-b-0 ${selected ? 'bg-blue-50' : 'hover:bg-muted/50'}`} data-testid={`bom-lot-${mIdx}-${lIdx}`}>
                                          <input type="checkbox" checked={selected} onChange={() => toggleLot(lot, mat)} className="w-3 h-3 cursor-pointer" />
                                          <span className="flex-1 min-w-0">
                                            <span className="font-mono font-medium">{lot.reference_number || 'No Ref'}</span>
                                            <span className="text-muted-foreground ml-1.5">Dt: {lot.received_date}</span>
                                            <span className="ml-1.5">Avl: <span className="text-emerald-600 font-medium">{lot.remaining_quantity}</span></span>
                                          </span>
                                          {selected && (
                                            <div className="flex items-center gap-1 shrink-0">
                                              <Label className="text-[10px] text-blue-600">Qty:</Label>
                                              <Input type="number" step="0.01" min="0.01" max={lot.remaining_quantity}
                                                value={selLine?.selected_quantity ?? lot.remaining_quantity}
                                                onChange={e => updateLotQty(lot.lot_id, e.target.value, lot.remaining_quantity)}
                                                onClick={e => e.stopPropagation()}
                                                className="h-5 w-20 text-[10px] text-right font-mono"
                                                data-testid={`bom-lot-qty-${mIdx}-${lIdx}`} />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                {selectedForMat.length > 0 && (
                                  <p className="text-[10px] text-blue-600 mt-0.5 pl-2">{selectedForMat.length} lot(s) selected — {selectedQtySum.toFixed(2)} {mat.unit_of_measure}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* BOM Consumption Preview — only for purchased RM */}
                  {hasPurchased && purchasedMats.length > 0 && parseFloat(createForm.quantity) > 0 && (
                    <div className="border rounded-md p-2 bg-slate-50/80" data-testid="bom-consumption-preview">
                      <p className="text-[11px] font-medium mb-1.5">RM Consumption Preview (for {createForm.quantity} units)</p>
                      <div className="space-y-1">
                        {purchasedMats.map((mat, mIdx) => {
                          const required = mat.quantity_per_unit * parseFloat(createForm.quantity);
                          const netAvl = mat.net_available ?? Math.max(0, (mat.store_balance || 0) - (mat.committed_qty || 0));
                          const shortage = required > netAvl;
                          return (
                            <div key={mat.item_id} className="flex items-center gap-2 text-[10px]" data-testid={`bom-preview-${mIdx}`}>
                              <span className="flex-1 truncate font-medium">{mat.item_name} <span className="font-mono text-muted-foreground">({mat.item_unique_id})</span></span>
                              <span className="tabular-nums whitespace-nowrap">
                                Need: <span className="font-medium">{required.toFixed(2)}</span>
                              </span>
                              <span className="tabular-nums whitespace-nowrap">
                                Net Avl: <span className={`font-medium ${shortage ? 'text-red-600' : 'text-emerald-600'}`}>{netAvl}</span>
                              </span>
                              {shortage ? (
                                <span className="text-red-600 font-medium whitespace-nowrap" data-testid={`bom-preview-shortage-${mIdx}`}>
                                  Short: {(required - netAvl).toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-emerald-600 whitespace-nowrap">OK</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {purchasedMats.some(mat => {
                        const netAvl = mat.net_available ?? Math.max(0, (mat.store_balance || 0) - (mat.committed_qty || 0));
                        return mat.quantity_per_unit * parseFloat(createForm.quantity) > netAvl;
                      }) && (
                        <p className="text-[10px] text-red-600 font-medium mt-1.5 border-t pt-1" data-testid="bom-shortage-warning">
                          Insufficient stock for one or more materials. Work order creation will be rejected.
                        </p>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Quantity *</Label>
                {(() => {
                  const hasChallanItems = (createForm.challan_line_items || []).length > 0;
                  const hasLots = (createForm.selected_lots || []).length > 0;
                  const isAutoComputed = hasChallanItems || hasLots;
                  return (
                    <>
                      <Input type="number" value={createForm.quantity}
                        readOnly={isAutoComputed}
                        onChange={e => {
                          if (isAutoComputed) return;
                          setCreateForm(f => ({...f, quantity: e.target.value}));
                        }}
                        className={`h-8 text-xs ${isAutoComputed ? 'bg-muted cursor-not-allowed font-bold' : ''}`} data-testid="wo-quantity" />
                      {hasChallanItems && (
                        <p className="text-[10px] text-blue-600 mt-0.5">Auto-calculated from challan selections. Modify quantities in the challan items above.</p>
                      )}
                      {!hasChallanItems && hasLots && (
                        <p className="text-[10px] text-blue-600 mt-0.5">Auto-calculated from selected RM lots & BOM ratios</p>
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={createForm.priority} onValueChange={v => setCreateForm(f => ({...f, priority: v}))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="wo-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Planned Start</Label>
                <Input type="date" value={createForm.planned_start_date} onChange={e => setCreateForm(f => ({...f, planned_start_date: e.target.value}))}
                  className="h-8 text-xs" data-testid="wo-start-date" />
              </div>
              <div>
                <Label className="text-xs">Planned End</Label>
                <Input type="date" value={createForm.planned_end_date} onChange={e => setCreateForm(f => ({...f, planned_end_date: e.target.value}))}
                  className="h-8 text-xs" data-testid="wo-end-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Route Card Number</Label>
              <Input value={createForm.route_card_number} onChange={e => setCreateForm(f => ({...f, route_card_number: e.target.value}))}
                className="h-8 text-xs font-mono" data-testid="wo-rc-number" />
            </div>
            {createForm.own_consumption ? (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2.5 text-[11px] text-amber-800" data-testid="wo-own-consumption-note">
                <span className="font-semibold">Own consumption WO</span> — Customer and Purchase Order are not required. This fixed asset is manufactured for internal use.
              </div>
            ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer *</Label>
                <SearchableSelect
                  options={[...customers.map(c => ({ value: c.id, label: c.customer_name, sub: c.gst_no || '', searchStr: `${c.customer_name} ${c.gst_no || ''}` }))]}
                  value={createForm.customer_id || ''}
                  onSelect={v => {
                    const cust = customers.find(c => c.id === v);
                    setCreateForm(f => ({ ...f, customer_id: v || '', customer_name: cust ? cust.customer_name : '', po_id: '', po_number: '' }));
                    loadOpenPOs(v);
                  }}
                  placeholder="Select customer (required)..."
                  emptyText="No customers found."
                  testId="wo-customer-select"
                />
              </div>
              <div>
                <Label className="text-xs">Customer WO Number</Label>
                <Input value={createForm.customer_wo_number} onChange={e => setCreateForm(f => ({...f, customer_wo_number: e.target.value}))}
                  placeholder="Customer's WO/Ref#"
                  className="h-8 text-xs font-mono" data-testid="wo-customer-wo-number" />
              </div>
            </div>
            )}
            <div>
              <Label className="text-xs">Project Number</Label>
              <Input value={createForm.project_number} onChange={e => setCreateForm(f => ({...f, project_number: e.target.value}))}
                className="h-8 text-xs font-mono" data-testid="wo-project" />
            </div>
            {createForm.customer_id && (() => {
              const isJobwork = (createForm.challan_line_items && createForm.challan_line_items.length > 0)
                || !!createForm.inbound_challan_id;
              return (
                <div>
                  <Label className="text-xs">
                    Purchase Order {isJobwork ? <span className="text-muted-foreground font-normal">(optional)</span> : <span className="text-red-600">*</span>}
                  </Label>
                  <SearchableSelect
                    options={[{ value: '', label: 'None' }, ...openPOs.map(po => ({
                      value: po.id,
                      label: po.po_number,
                      sub: `${po.customer_name} | ${po.status} | ${po.items?.length || 0} items`,
                      searchStr: `${po.po_number} ${po.project_number || ''} ${po.customer_name}`,
                    }))]}
                    value={createForm.po_id || ''}
                    onSelect={v => {
                      const po = openPOs.find(p => p.id === v);
                      setCreateForm(f => ({ ...f, po_id: v || '', po_number: po ? po.po_number : '' }));
                    }}
                    placeholder="Search PO number..."
                    emptyText="No open POs for this customer."
                    testId="wo-po-select"
                  />
                  {!isJobwork && !createForm.po_id && (
                    <p className="text-[10px] text-red-600 mt-0.5" data-testid="po-required-hint">
                      Required for own-manufactured items — flows the customer PO to dispatch manifest & DC.
                    </p>
                  )}
                </div>
              );
            })()}
            <div>
              <Label className="text-xs">Remarks</Label>
              <Input value={createForm.remarks} onChange={e => setCreateForm(f => ({...f, remarks: e.target.value}))}
                className="h-8 text-xs" data-testid="wo-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="save-wo-btn">
              {creating ? 'Creating...' : 'Create Work Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Production Receipt Dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Production Receipt - {receiptOrder?.wo_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="p-3 rounded-md bg-muted/50 text-xs space-y-1">
              <p><span className="text-muted-foreground">Item:</span> <strong>{receiptOrder?.item_name}</strong></p>
              <p><span className="text-muted-foreground">Ordered:</span> {receiptOrder?.quantity} | <span className="text-muted-foreground">Produced so far:</span> {receiptOrder?.produced_quantity || 0}</p>
              {receiptOrder?.customer_name && <p><span className="text-muted-foreground">Customer:</span> <strong>{receiptOrder?.customer_name}</strong></p>}
              {receiptOrder?.customer_wo_number && <p><span className="text-muted-foreground">Customer WO:</span> <strong>{receiptOrder?.customer_wo_number}</strong></p>}
              {(receiptOrder?.challan_line_items || []).length > 0 && (
                <p><span className="text-muted-foreground">Challan Lines:</span> {receiptOrder.challan_line_items.map((cli, i) => (
                  <span key={i} className="inline-block mr-1.5">
                    <Badge variant="outline" className="text-[9px]">{cli.challan_number} #{cli.line_index + 1}: {cli.item_name} ({cli.selected_quantity || cli.quantity} {cli.unit_of_measure || 'Nos'})</Badge>
                  </span>
                ))}</p>
              )}
              <p><span className="text-muted-foreground">BOM Items:</span> {(receiptOrder?.bill_of_materials || []).length} materials will be consumed proportionally</p>
            </div>
            <div>
              <Label className="text-xs">Produced Quantity (OK) *</Label>
              <Input type="number" value={receiptForm.produced_quantity} onChange={e => {
                const produced = parseFloat(e.target.value) || 0;
                const perUnitScrap = (receiptOrder?.output_items || []).filter(o => o.output_type === 'scrap').reduce((s, o) => s + (o.quantity || 0), 0);
                setReceiptForm(f => ({...f, produced_quantity: e.target.value, scrap_quantity: String(perUnitScrap * produced)}));
                // Auto-distribute quantity evenly across trace entries if they exist
                if (traceEntries.length > 0 && produced > 0) {
                  const each = Math.floor((produced / traceEntries.length) * 100) / 100;
                  const lastExtra = +(produced - each * (traceEntries.length - 1)).toFixed(2);
                  setTraceEntries(prev => prev.map((te, idx) => {
                    let qty = idx === prev.length - 1 ? lastExtra : each;
                    // Cap at max_quantity (issued qty from challan line)
                    if (te.max_quantity && qty > te.max_quantity) qty = te.max_quantity;
                    return { ...te, quantity: qty };
                  }));
                }
              }}
                className="h-8 text-xs" data-testid="pr-produced" />
            </div>

            {/* Trace Entries Section */}
            {traceEntries.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Traceability Split (per Customer WO / Trace ID)</Label>
                  <span className="text-[10px] text-muted-foreground">
                    OK: {traceEntries.reduce((s, te) => s + (parseFloat(te.quantity) || 0), 0).toFixed(2)} / {receiptForm.produced_quantity || 0}
                    {' · '}
                    Q: {traceEntries.reduce((s, te) => s + (parseFloat(te.quarantined_quantity) || 0), 0).toFixed(2)} / {receiptForm.rejected_quantity || 0}
                  </span>
                </div>
                {(() => {
                  const traceOkTotal = traceEntries.reduce((s, te) => s + (parseFloat(te.quantity) || 0), 0);
                  const traceQTotal = traceEntries.reduce((s, te) => s + (parseFloat(te.quarantined_quantity) || 0), 0);
                  const produced = parseFloat(receiptForm.produced_quantity) || 0;
                  const quarantined = parseFloat(receiptForm.rejected_quantity) || 0;
                  const okMismatch = (produced > 0 || traceOkTotal > 0) && Math.abs(traceOkTotal - produced) > 0.01;
                  const qMismatch = (quarantined > 0 || traceQTotal > 0) && Math.abs(traceQTotal - quarantined) > 0.01;
                  return (okMismatch || qMismatch) ? (
                    <div className="text-[10px] text-red-600 font-medium space-y-0.5" data-testid="trace-mismatch-warning">
                      {okMismatch && (
                        <p>OK trace total ({traceOkTotal.toFixed(2)}) ≠ Produced ({produced}). Adjust per-trace OK qty.</p>
                      )}
                      {qMismatch && (
                        <p>Quarantine trace total ({traceQTotal.toFixed(2)}) ≠ Quarantined ({quarantined}). Adjust per-trace Q qty.</p>
                      )}
                    </div>
                  ) : null;
                })()}
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-1.5 text-left font-medium">Trace ID / Customer WO</th>
                        <th className="p-1.5 text-left font-medium">Type</th>
                        <th className="p-1.5 text-right font-medium w-16">Issued</th>
                        <th className="p-1.5 text-right font-medium w-20">OK Qty</th>
                        <th className="p-1.5 text-right font-medium w-20">Quarantine Qty</th>
                        <th className="p-1.5 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {traceEntries.map((te, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="p-1.5">
                            <Input
                              value={te.trace_id}
                              onChange={e => setTraceEntries(prev => prev.map((t, i) => i === idx ? {
                                ...t,
                                trace_id: e.target.value,
                                trace_label: t.trace_type === 'customer_wo' ? `Customer WO: ${e.target.value}` : t.trace_label
                              } : t))}
                              className="h-6 text-xs"
                              placeholder="Trace ID"
                              data-testid={`trace-id-${idx}`}
                            />
                            {te.item_name && <span className="text-[9px] text-muted-foreground block mt-0.5">{te.item_name}</span>}
                          </td>
                          <td className="p-1.5">
                            <Badge variant="outline" className="text-[9px]">
                              {te.trace_type === 'customer_wo' ? 'Cust WO' : te.trace_type === 'challan_line' ? 'Challan' : 'Auto'}
                            </Badge>
                          </td>
                          <td className="p-1.5 text-right">
                            {te.max_quantity ? (
                              <span className="text-[10px] font-mono text-muted-foreground">{te.max_quantity}</span>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-1.5 text-right">
                            <Input
                              type="number"
                              value={te.quantity}
                              onChange={e => {
                                const val = parseFloat(e.target.value) || 0;
                                const qq = parseFloat(te.quarantined_quantity) || 0;
                                if (te.max_quantity && (val + qq) > te.max_quantity) {
                                  toast.error(`OK + Quarantine cannot exceed issued qty (${te.max_quantity})`);
                                }
                                setTraceEntries(prev => prev.map((t, i) => i === idx ? { ...t, quantity: e.target.value } : t));
                              }}
                              max={te.max_quantity || undefined}
                              className={`h-6 text-xs w-20 text-right ml-auto ${te.max_quantity && ((parseFloat(te.quantity) || 0) + (parseFloat(te.quarantined_quantity) || 0)) > te.max_quantity ? 'border-red-400 text-red-600' : ''}`}
                              data-testid={`trace-qty-${idx}`}
                            />
                          </td>
                          <td className="p-1.5 text-right">
                            <Input
                              type="number"
                              value={te.quarantined_quantity ?? 0}
                              onChange={e => {
                                const val = parseFloat(e.target.value) || 0;
                                const ok = parseFloat(te.quantity) || 0;
                                if (te.max_quantity && (val + ok) > te.max_quantity) {
                                  toast.error(`OK + Quarantine cannot exceed issued qty (${te.max_quantity})`);
                                }
                                setTraceEntries(prev => prev.map((t, i) => i === idx ? { ...t, quarantined_quantity: e.target.value } : t));
                              }}
                              max={te.max_quantity || undefined}
                              className={`h-6 text-xs w-20 text-right ml-auto ${te.max_quantity && ((parseFloat(te.quantity) || 0) + (parseFloat(te.quarantined_quantity) || 0)) > te.max_quantity ? 'border-red-400 text-red-600' : 'border-amber-300'}`}
                              data-testid={`trace-qq-${idx}`}
                            />
                          </td>
                          <td className="p-1.5 text-center">
                            <button
                              type="button"
                              className="text-red-400 hover:text-red-600 transition-colors"
                              onClick={() => setTraceEntries(prev => prev.filter((_, i) => i !== idx))}
                              title="Remove trace"
                              data-testid={`trace-remove-${idx}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-[10px] h-6"
                  onClick={() => setTraceEntries(prev => [...prev, {
                    trace_id: `AUTO-${Date.now()}`,
                    trace_type: 'auto',
                    trace_label: 'Auto-generated',
                    quantity: 0,
                    quarantined_quantity: 0,
                    challan_id: '', challan_number: '', challan_line_index: null,
                  }])}
                  data-testid="add-trace-entry"
                >
                  + Add Trace Entry
                </Button>
              </div>
            )}

            {traceEntries.length === 0 && (receiptOrder?.challan_line_items || []).length === 0 && !receiptOrder?.customer_wo_number && (
              <div className="text-[10px] text-muted-foreground p-2 bg-muted/30 rounded">
                No challan line items or customer WO linked to this work order. A single auto-generated trace will be created.
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="text-[10px] h-4 px-1"
                  onClick={() => setTraceEntries([{
                    trace_id: '',
                    trace_type: 'customer_wo',
                    trace_label: '',
                    quantity: parseFloat(receiptForm.produced_quantity) || 0,
                    challan_id: '', challan_number: '', challan_line_index: null,
                  }])}
                  data-testid="add-manual-trace"
                >
                  Add manual trace?
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Scrap Quantity {(() => {
                  const perUnitScrap = (receiptOrder?.output_items || []).filter(o => o.output_type === 'scrap').reduce((s, o) => s + (o.quantity || 0), 0);
                  const scrapUnit = (receiptOrder?.output_items || []).find(o => o.output_type === 'scrap')?.unit_of_measure || '';
                  return perUnitScrap > 0 ? <span className="text-amber-600 font-normal">(BOM: {perUnitScrap} {scrapUnit}/unit)</span> : null;
                })()}</Label>
                <Input type="number" value={receiptForm.scrap_quantity} onChange={e => setReceiptForm(f => ({...f, scrap_quantity: e.target.value}))}
                  className="h-8 text-xs" data-testid="pr-scrap" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">Quarantined Quantity
                  <span className="text-[10px] text-amber-700 font-normal">(not-OK parts → MRB)</span>
                </Label>
                <Input type="number" value={receiptForm.rejected_quantity} onChange={e => {
                  const q = parseFloat(e.target.value) || 0;
                  setReceiptForm(f => ({...f, rejected_quantity: e.target.value}));
                  // Auto-distribute quarantine evenly across trace entries
                  if (traceEntries.length > 0 && q >= 0) {
                    const each = Math.floor((q / traceEntries.length) * 100) / 100;
                    const lastExtra = +(q - each * (traceEntries.length - 1)).toFixed(2);
                    setTraceEntries(prev => prev.map((te, idx) => {
                      let qq = idx === prev.length - 1 ? lastExtra : each;
                      // Cap so OK + Quarantine ≤ max_quantity
                      const ok = parseFloat(te.quantity) || 0;
                      if (te.max_quantity && (qq + ok) > te.max_quantity) qq = Math.max(0, te.max_quantity - ok);
                      return { ...te, quarantined_quantity: qq };
                    }));
                  }
                }}
                  className="h-8 text-xs" data-testid="pr-quarantined" />
                <p className="text-[10px] text-muted-foreground mt-0.5">Moves to Quarantine Area; an MRB case will auto-open for disposition. Distributed per trace ID for customer-WO traceability.</p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Input value={receiptForm.remarks} onChange={e => setReceiptForm(f => ({...f, remarks: e.target.value}))}
                className="h-8 text-xs" data-testid="pr-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptOpen(false)}>Cancel</Button>
            <Button onClick={handleProductionReceipt} disabled={receiptLoading} data-testid="save-receipt-btn">
              {receiptLoading ? 'Processing...' : 'Record Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Work Order Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Work Order - {editOrder?.wo_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-md bg-muted/50 text-xs space-y-1">
              <p><span className="text-muted-foreground">Item:</span> <strong>{editOrder?.item_name}</strong> <span className="font-mono text-muted-foreground">({editOrder?.item_unique_id})</span></p>
              <p><span className="text-muted-foreground">Route:</span> {editOrder?.route_name}</p>
              {(editOrder?.produced_quantity || 0) > 0 && (
                <p><span className="text-muted-foreground">Produced so far:</span> <strong>{editOrder?.produced_quantity}</strong></p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Quantity *</Label>
                <Input type="number" value={editForm.quantity} onChange={e => setEditForm(f => ({...f, quantity: e.target.value}))}
                  min={editOrder?.produced_quantity || 0}
                  className="h-8 text-xs" data-testid="edit-wo-quantity" />
                {(editOrder?.produced_quantity || 0) > 0 && (
                  <p className="text-[10px] text-amber-600 mt-0.5">Min: {editOrder?.produced_quantity} (already produced)</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={editForm.priority} onValueChange={v => setEditForm(f => ({...f, priority: v}))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="edit-wo-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Planned Start</Label>
                <Input type="date" value={editForm.planned_start_date} onChange={e => setEditForm(f => ({...f, planned_start_date: e.target.value}))}
                  className="h-8 text-xs" data-testid="edit-wo-start-date" />
              </div>
              <div>
                <Label className="text-xs">Planned End</Label>
                <Input type="date" value={editForm.planned_end_date} onChange={e => setEditForm(f => ({...f, planned_end_date: e.target.value}))}
                  className="h-8 text-xs" data-testid="edit-wo-end-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Route Card Number</Label>
              <Input value={editForm.route_card_number} onChange={e => setEditForm(f => ({...f, route_card_number: e.target.value}))}
                className="h-8 text-xs font-mono" data-testid="edit-wo-rc-number" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer</Label>
                <SearchableSelect
                  options={[{ value: '', label: 'None' }, ...editCustomers.map(c => ({ value: c.id, label: c.customer_name, sub: c.gst_no || '', searchStr: `${c.customer_name} ${c.gst_no || ''}` }))]}
                  value={editForm.customer_id || ''}
                  onSelect={v => {
                    const cust = editCustomers.find(c => c.id === v);
                    setEditForm(f => ({ ...f, customer_id: v || '', customer_name: cust ? cust.customer_name : '', po_id: '', po_number: '' }));
                    loadEditPOs(v);
                  }}
                  placeholder="Search customers..."
                  emptyText="No customers found."
                  testId="edit-wo-customer-select"
                />
              </div>
              <div>
                <Label className="text-xs">Customer WO Number</Label>
                <Input value={editForm.customer_wo_number} onChange={e => setEditForm(f => ({...f, customer_wo_number: e.target.value}))}
                  placeholder="Customer's WO/Ref#"
                  className="h-8 text-xs font-mono" data-testid="edit-wo-customer-wo-number" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Project Number</Label>
              <Input value={editForm.project_number} onChange={e => setEditForm(f => ({...f, project_number: e.target.value}))}
                className="h-8 text-xs font-mono" data-testid="edit-wo-project" />
            </div>
            {editForm.customer_id && (
              <div>
                <Label className="text-xs">Purchase Order</Label>
                <SearchableSelect
                  options={[{ value: '', label: 'None' }, ...editOpenPOs.map(po => ({
                    value: po.id,
                    label: po.po_number,
                    sub: `${po.customer_name} | ${po.status} | ${po.items?.length || 0} items`,
                    searchStr: `${po.po_number} ${po.project_number || ''} ${po.customer_name}`,
                  }))]}
                  value={editForm.po_id || ''}
                  onSelect={v => {
                    const po = editOpenPOs.find(p => p.id === v);
                    setEditForm(f => ({ ...f, po_id: v || '', po_number: po ? po.po_number : '' }));
                  }}
                  placeholder="Search PO number..."
                  emptyText="No open POs for this customer."
                  testId="edit-wo-po-select"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Remarks</Label>
              <Input value={editForm.remarks} onChange={e => setEditForm(f => ({...f, remarks: e.target.value}))}
                className="h-8 text-xs" data-testid="edit-wo-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            {isAdmin && ['released', 'in_progress'].includes(editOrder?.status) && (
              <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 mr-auto"
                onClick={() => { setForceCloseTarget(editOrder); setForceCloseReason(''); }}
                data-testid="edit-force-close-wo-btn">
                <Lock className="w-3.5 h-3.5 mr-1" /> Force Close
              </Button>
            )}
            <Button onClick={handleEdit} disabled={editing} data-testid="save-edit-wo-btn">
              {editing ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Close Work Order (admin-only) */}
      <AlertDialog open={!!forceCloseTarget} onOpenChange={(open) => !open && !forceClosing && setForceCloseTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force Close Work Order</AlertDialogTitle>
            <AlertDialogDescription>
              Force <strong>{forceCloseTarget?.wo_number}</strong> ({forceCloseTarget?.item_name}) to <strong>completed</strong>,
              even though received qty (produced {forceCloseTarget?.produced_quantity || 0} + quarantine {forceCloseTarget?.quarantined_quantity || 0} + scrap {forceCloseTarget?.scrap_quantity || 0})
              is short of the ordered {forceCloseTarget?.quantity}. Production figures are left unchanged. This is audit-logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <Label className="text-xs">Reason <span className="text-red-500">*</span></Label>
            <Textarea value={forceCloseReason} onChange={e => setForceCloseReason(e.target.value)}
              placeholder="e.g. 1 unit lost to process/yield loss, not recoverable"
              className="text-xs" rows={2} data-testid="force-close-reason-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={forceClosing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleForceClose(); }} disabled={forceClosing}
              className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-force-close-wo">
              {forceClosing ? 'Closing...' : 'Force Close'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reopen Work Order (admin-only) */}
      <AlertDialog open={!!reopenTarget} onOpenChange={(open) => !open && !reopening && setReopenTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen Work Order</AlertDialogTitle>
            <AlertDialogDescription>
              Reopen <strong>{reopenTarget?.wo_number}</strong> ({reopenTarget?.item_name}) so the remaining quantity
              can be received (produced {reopenTarget?.produced_quantity || 0} of {reopenTarget?.quantity}).
              Production figures are kept; the completion/force-close markers are cleared. This is audit-logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopening}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleReopen(); }} disabled={reopening}
              className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-reopen-wo">
              {reopening ? 'Reopening...' : 'Reopen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Work Order Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Work Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.wo_number}</strong> ({deleteTarget?.item_name})?
              This cannot be undone. Only work orders with no production receipts can be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteWO} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-wo">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
